
import { translate } from "../translations";
import { tranCase } from "./case-utils";



export function replaceSecondUpper(str: string) {
    // Initialize a counter for uppercase characters
    let upperCount = 0;
    let tempStr = str;
    // Loop through each character of the string
    for (let i = 0; i < str.length; i++) {
        // If the character is uppercase
        if (str[i] === str[i].toUpperCase()) {
            // Increment the counter
            upperCount++;
            // If the counter is 2, replace the character with lowercase and return the result
            if (upperCount === 2) {
                upperCount = 0;
                str = str.slice(0, i) + str[i].toLowerCase() + str.slice(i + 1);

            }
        } else {
            upperCount = 0;
        }
    }
    // If no second uppercase character is found, return the original string
    return str;
}


/**
 * Removes colon-delimited prefixes from an error message to surface the most specific segment.
 * @param error Raw error string containing possible namespace prefixes.
 * @returns Error message without leading prefixes.
 */
export const cleanErrorMessage = (error: string) => {
    if (error) {
        let index = error.indexOf(':');
        do {
            if (index > -1) {
                error = error.substr(index + 1).trimLeft().trimRight();
            }
            index = error.indexOf(':');
        }
        while (index > -1)
    }
    return error;
}
/**
 * Formats a byte count into a human-readable size string.
 * @param bytes Number of bytes to convert.
 * @param si When true, uses SI units (power of 1000); otherwise uses binary units (power of 1024).
 * @param dp Number of decimal places to display.
 * @returns Formatted size string such as `1.5 MB`.
 */
export function humanFileSize(bytes: number, si = false, dp = 1) {
    const thresh = si ? 1000 : 1024;

    if (Math.abs(bytes) < thresh) {
        return bytes + ' B';
    }

    const units = si
        ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
        : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
    let u = -1;
    const r = 10 ** dp;

    do {
        bytes /= thresh;
        ++u;
    } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);


    return bytes.toFixed(dp) + ' ' + units[u];
}

/**
 * Generates a random RGB color hex string without a leading hash.
 * @returns Six-character hexadecimal color value.
 */
export const randomColor = () => {
    return Math.floor(Math.random() * 16777215).toString(16);
}


/**
 * Converts an array of string values into translated option objects sorted alphabetically.
 * @param array Raw option values to transform.
 * @param prefix Translation key prefix applied before converting to snake case.
 * @returns Array of option objects containing translated `name` and original `value`.
 */
export const stringArrayToOptions = (array: string[], prefix: string = "TCM") => {

    let items = array.map(x => {
        return { name: translate(`${prefix}_${tranCase(x)}`), value: x }
    })
    items.sort((a, b) => a.name.localeCompare(b.name))
    return items;


}

/**
 * Converts PascalCase or snake/kebab identifiers into a title-cased label with spaces.
 * @param pascalCase Identifier string to convert.
 * @returns Title-cased label suitable for UI display.
 */
export function pascalCaseToLabel(pascalCase: string): string {
    // Replace underscores/dashes with spaces, then add spaces for case transitions
    const normalized = (pascalCase ?? "")
        .replace(/[_-]+/g, " ")
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");

    return normalized
        .trim()
        .replace(/\s+/g, " ")
        .split(" ")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
}

/**
 * Replaces all underscores with dashes in the provided string.
 * @param value Text that may contain underscores.
 * @returns Modified string with underscores swapped for dashes.
 */
export function replaceUnderscoresWithDashes(value: string): string {
    return value.replace(/_/g, "-");
}


/**
 * Removes the final file extension from a path or filename in a browser-safe way, mirroring Node's `path.parse().name`.
 * @param value Path or filename that may include query or hash components.
 * @returns Basename without the trailing extension segment (e.g., `file.tar.gz` → `file.tar`).
 */
export const stripExt = (value: string): string => {
    if (!value) return value;
    // Drop any query/hash (lightweight handling for URLs)
    const clean = value.split(/[?#]/)[0];
    // Work only on the final path segment
    const base = clean.split(/[/\\]/).pop() || clean;
    const dot = base.lastIndexOf('.');
    // No dot or hidden file like .env
    if (dot <= 0) return base;
    return base.substring(0, dot);
};


